import { storedToken } from './api.ts'

/**
 * Who the stored session belongs to.
 *
 * The token already carries the email, and reading it here is what keeps the
 * user menu on the page after a reload. Setting the name only at sign-in --
 * which is what this app did -- means a refresh leaves the menu with nothing
 * to render, and the only way to log out disappears until you sign in again.
 *
 * Read for display only. Nothing here is a permission check: the claims are
 * the server's to verify, and every route does.
 */
export function sessionEmail(): string | undefined {
  const claims = decode(storedToken())
  const email = claims?.['email']
  return typeof email === 'string' && email !== '' ? email : undefined
}

/**
 * Whether this session operates the system rather than using it.
 *
 * For deciding what to *offer*, never for deciding what to allow. Every screen
 * this gates is already gated on the server -- `/api/synonyms/*` and the rest
 * answer 404 to anybody else -- so this exists so a pharmacist is not shown a
 * door that will not open, which is a courtesy rather than a boundary.
 *
 * Read from the token so it survives a reload, like the email above.
 */
export function sessionIsSuperAdmin(token: string | undefined = storedToken()): boolean {
  return decode(token)?.['role'] === 'super_admin'
}

/** The session's expiry, in epoch seconds, when the token declares one. */
export function sessionExpiry(): number | undefined {
  const exp = decode(storedToken())?.['exp']
  return typeof exp === 'number' ? exp : undefined
}

/**
 * A JWT payload, unverified.
 *
 * Deliberately not a signature check -- doing one here would need the secret
 * in the browser, and would prove nothing the server does not already prove on
 * every request.
 */
function decode(token: string | undefined): Record<string, unknown> | undefined {
  if (token === undefined) return undefined
  const payload = token.split('.')[1]
  if (payload === undefined) return undefined
  try {
    // base64url -> base64, then pad: atob rejects both differences.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    // A token we cannot read is a token we do not trust to name anybody.
    return undefined
  }
}
